import type { ChartEvent, Chart, ChartArea } from 'chart.js'
import { formatMemberValue } from './analyticsFormat'
import { EMPTY_VALUE } from '~/constants/cube'
import type {
  ChartData,
  OpenDrilldownEventBody,
  TrendChartData
} from '~/types/analytics'
import { FormatContext } from '~/types/analyticsFormat'
import { COLOR_TRANSPARENCY_SUFFIX, SCALES_OPTIONS } from '~/constants/chart'
import { buildObjectFromEntries } from '~/types/utils'

export const buildOpenDrilldownEventBody = (
  clickEvent: ChartEvent,
  chart: Chart,
  chartData: ChartData
): OpenDrilldownEventBody | null => {
  const elementPosition = chart.getElementsAtEventForMode(
    clickEvent.native!,
    'nearest',
    { intersect: true },
    true
  )[0]

  if (!elementPosition) {
    return null
  }

  const elementDataset = chartData.datasets[elementPosition.datasetIndex]
  if (!elementDataset) {
    return null
  }

  const dimensionValue = chartData.labels[elementPosition.index]!.value

  return {
    dimensionValue: dimensionValue !== null ? dimensionValue.toString() : null,
    measureTitle: elementDataset.measureTitle,
    groupValue: elementDataset.groupValue?.toString() ?? null
  }
}

export const getColorPalette = (size: number, baseColors: string[]) => {
  return Array.from(
    { length: size },
    (_, index) => baseColors[index % baseColors.length]!
  )
}

export const getColorGradientPalette = (size: number, baseColors: string[]) => {
  return Array.from({ length: size }, (_, index) => ({
    bottom: baseColors[index % (baseColors.length - 1)]!,
    top: baseColors[(index % (baseColors.length - 1)) + 1]!
  }))
}

export const removeZeroInData = <T extends ChartData | TrendChartData>(
  chartData: T
): T => {
  if (!chartData.datasets[0] || chartData.datasets.length > 1) {
    return chartData
  }

  const indexesToRemove = chartData.datasets[0].data.reduce<number[]>(
    (indexes, dataElement, index) =>
      dataElement === null || dataElement === 0 ? [...indexes, index] : indexes,
    []
  )

  return {
    ...chartData,
    datasets: [
      {
        ...chartData.datasets[0],
        data: chartData.datasets[0].data.filter(
          (_, index) => !indexesToRemove.includes(index)
        )
      }
    ],
    labels: chartData.labels.filter(
      (_, index) => !indexesToRemove.includes(index)
    )
  }
}

export const buildYScales = ({
  chartData,
  isStacked,
  isMultiMeasures
}: {
  chartData: ChartData
  isStacked: boolean
  isMultiMeasures: boolean
}) => {
  return buildObjectFromEntries(
    chartData.datasets.map((dataset, index) => [
      chartData.datasets.length === 1 ? 'y' : `y${dataset.measureTitle}`,
      {
        ...SCALES_OPTIONS.y,
        type: 'linear',
        position:
          chartData.labels.length > 14
            ? 'left'
            : isMultiMeasures && index % 2 === 1
            ? 'right'
            : 'left',
        stack: dataset.measureTitle,
        stacked: isStacked,
        beginAtZero: true,
        ticks: {
          ...SCALES_OPTIONS.y.ticks,
          display: true,
          font: {
            size: 14
          },
          maxTicksLimit: 8,
          callback: (value: string) => {
            return formatMemberValue(
              value,
              dataset.format,
              FormatContext.CHART,
              false
            )
          }
        }
      }
    ])
  )
}

export const generateColorGradient = ({
  ctx,
  chartArea,
  color,
  isTransparent = false
}: {
  ctx: CanvasRenderingContext2D
  chartArea: ChartArea
  color: { bottom: string; top: string }
  isTransparent?: boolean
}): CanvasGradient => {
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  )

  gradient.addColorStop(
    0,
    isTransparent ? color.top + COLOR_TRANSPARENCY_SUFFIX : color.top
  )

  gradient.addColorStop(
    1,
    isTransparent ? color.bottom + COLOR_TRANSPARENCY_SUFFIX : color.bottom
  )

  return gradient
}

export const processLargeDataTrim = (
  chartData: TrendChartData,
  limit: number
) => {
  if (!chartData.datasets[0] || chartData.datasets[0].data.length <= limit) {
    return chartData
  }

  const indexOfNullValue = chartData.labels.indexOf(EMPTY_VALUE)

  let indexesToGroup: number[] = []
  if (indexOfNullValue === -1 || indexOfNullValue >= limit) {
    indexesToGroup = chartData.datasets[0].data
      .map((_, index) => index)
      .filter(index => index >= limit - 1)
  } else {
    indexesToGroup = [
      indexOfNullValue,
      ...chartData.datasets[0].data
        .map((_, index) => index)
        .filter(index => index >= limit)
    ]
  }

  if (indexesToGroup.length === 0) {
    return chartData
  }

  const groupedValues = chartData.datasets[0].data
    .filter((_, i) => indexesToGroup.includes(i))
    .reduce<number>((total, datasetValue) => total + Number(datasetValue), 0)

  const { $t } = useNuxtApp()

  return {
    ...chartData,
    datasets: [
      {
        ...chartData.datasets[0],
        data: [
          ...chartData.datasets[0].data.filter(
            (_, i) => !indexesToGroup.includes(i)
          ),
          ...(groupedValues > 0 ? [groupedValues] : [])
        ]
      }
    ],
    labels: [
      ...chartData.labels.filter((_, i) => !indexesToGroup.includes(i)),
      ...(groupedValues > 0 ? [$t('cube.others')] : [])
    ]
  }
}

export const getDeformedValuesForFunnelChart = (
  datasets: { data: (number | null)[] }[]
): number[] => {
  const secondData = datasets[1]?.data[0]

  return datasets.map((dataset, index) => {
    // Reorganize chartData to fit one dataset/multiple values chartjs-funnel requirements
    // Deform chartData to render visible labels and chart

    if (!secondData) {
      return Number(dataset.data[0])
    }

    if (index === 0) {
      return secondData * 1.3
    }
    return Number(dataset.data[0]) + secondData / 10
  })
}
